/* ==========================================================================
   ATELIER TAHA — theme.js (vanilla, no dependencies)
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); }; };

  const fetchConfig = (body) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });

  const toast = (message) => {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3500);
  };

  /* ---------- Focus trap helpers ---------- */
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let trapHandler = null;
  const trapFocus = (container, initial) => {
    releaseFocus();
    trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const els = qsa(focusable, container).filter((el) => el.offsetParent !== null);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', trapHandler);
    (initial || qs(focusable, container))?.focus({ preventScroll: true });
  };
  const releaseFocus = () => { if (trapHandler) document.removeEventListener('keydown', trapHandler); trapHandler = null; };

  /* ---------- Generic drawer (menu, cart, filters) ---------- */
  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.opener?.setAttribute?.('aria-expanded', 'true');
      document.dispatchEvent(new CustomEvent('drawer:open'));
      // Focus the dialog itself (announced by its label) rather than the close button.
      const panel = qs('.drawer__panel', this);
      panel?.setAttribute('tabindex', '-1');
      setTimeout(() => trapFocus(panel, panel), 60);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      this.opener?.setAttribute?.('aria-expanded', 'false');
      releaseFocus();
      this.opener?.focus?.({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }
  customElements.define('theme-drawer', ThemeDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.getAttribute('data-drawer-open'));
    if (!drawer) return;
    e.preventDefault();
    drawer.open(opener);
  });

  /* ---------- Header: sticky, transparent, mega menu ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = qs('.header-wrapper', this);
      this.section = this.closest('.section-header-wrapper');
      this.transparent = this.wrapper?.classList.contains('header-wrapper--transparent-capable');
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;

      const setHeight = () => {
        const h = this.wrapper.offsetHeight;
        document.documentElement.style.setProperty('--header-offset', `${this.section?.classList.contains('is-static') ? 0 : h}px`);
      };
      setHeight();
      window.addEventListener('resize', debounce(setHeight, 150));

      this.onScroll();
      window.addEventListener('scroll', () => requestAnimationFrame(() => this.onScroll()), { passive: true });

      this.initMegaMenus();
      this.fitNavigation();
      window.addEventListener('resize', debounce(() => this.fitNavigation(), 150));
      document.fonts?.ready.then(() => this.fitNavigation());
      document.addEventListener('drawer:open', () => this.setTransparent(false));
      document.addEventListener('drawer:close', () => this.onScroll());
    }
    /* The desktop navigation must sit on one line. Menu titles are merchant content and can be
       long, so instead of a fixed breakpoint, switch to the menu drawer whenever they do not fit. */
    fitNavigation() {
      const nav = qs('.header__nav', this);
      const list = qs('.header__menu', this);
      if (!nav || !list) return;
      this.wrapper.classList.remove('is-compact');
      if (getComputedStyle(nav).display === 'none') return; // already the mobile layout
      this.wrapper.classList.toggle('is-compact', list.scrollWidth > nav.clientWidth + 1);
    }
    setTransparent(state) { if (this.transparent) this.wrapper.classList.toggle('is-transparent', state); }
    onScroll() {
      const y = window.scrollY;
      const menuOpen = !!qs('.header__menu li.is-open', this);
      this.setTransparent(y < 40 && !menuOpen && !document.body.classList.contains('overflow-hidden'));
      if (this.hideOnScroll && this.section) {
        const hide = y > this.lastY && y > 300 && !menuOpen;
        this.section.classList.toggle('is-hidden', hide);
      }
      this.lastY = y;
    }
    initMegaMenus() {
      qsa('[data-menu-item]', this).forEach((li) => {
        const trigger = qs('.header__menu-item', li);
        const panel = qs('.mega-menu', li);
        if (!panel) return;
        let closeTimer;
        const open = () => {
          clearTimeout(closeTimer);
          qsa('[data-menu-item].is-open', this).forEach((o) => o !== li && close(o));
          // Small dropdowns line up under their own menu item.
          if (panel.classList.contains('mega-menu--list')) panel.style.setProperty('--dd-left', `${Math.max(0, trigger.getBoundingClientRect().left - 24)}px`);
          li.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          this.setTransparent(false);
        };
        const close = (item = li) => {
          item.classList.remove('is-open');
          qs('.header__menu-item', item)?.setAttribute('aria-expanded', 'false');
          this.onScroll();
        };
        // Hover opening is for real mice only. Touch screens emit a compatibility mouseenter
        // just before the click, which would open the menu and let the same tap follow the link.
        li.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') open(); });
        li.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') closeTimer = setTimeout(() => close(), 180); });
        trigger.addEventListener('click', (e) => {
          if (trigger.tagName === 'BUTTON') { e.preventDefault(); li.classList.contains('is-open') ? close() : open(); return; }
          // Touch screens (e.g. a tablet in landscape) have no hover: the first tap opens the
          // menu so brands and models can be reached; a second tap follows the link.
          if (window.matchMedia('(hover: none)').matches && !li.classList.contains('is-open')) { e.preventDefault(); open(); }
        });
        // Keep aria-expanded in sync when the menu is revealed by keyboard focus (:focus-within).
        li.addEventListener('focusin', () => { if (!li.classList.contains('is-open')) open(); });
        // A tap outside closes it (touch screens never fire mouseleave).
        document.addEventListener('click', (e) => { if (li.classList.contains('is-open') && !li.contains(e.target)) close(); });
        li.addEventListener('keyup', (e) => { if (e.key === 'Escape') { close(); trigger.focus(); } });
        li.addEventListener('focusout', (e) => { if (!li.contains(e.relatedTarget)) close(); });

        // Brand → model panel switching
        const brands = qsa('[data-brand-index]', panel);
        const panels = qsa('[data-brand-panel]', panel);
        brands.forEach((b) => {
          const activate = () => {
            brands.forEach((x) => x.classList.toggle('is-active', x === b));
            panels.forEach((p) => p.classList.toggle('is-active', p.dataset.brandPanel === b.dataset.brandIndex));
          };
          b.addEventListener('mouseenter', activate);
          b.addEventListener('focusin', activate);
        });
      });
    }
  }
  customElements.define('site-header', SiteHeader);

  /* ---------- Mobile nav disclosure ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.mobile-nav__toggle');
    if (!btn) return;
    const target = document.getElementById(btn.getAttribute('aria-controls'));
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    if (target) target.hidden = expanded;
  });

  /* ---------- Announcement rotation ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = qsa('.announcement-bar__message', this);
      if (this.items.length < 2) return;
      this.index = 0;
      this.toggle = qs('[data-announcement-toggle]', this);
      // Paused by the button, or temporarily while the pointer or keyboard focus is on the bar.
      this.userPaused = false;
      this.hovered = false;
      this.toggle?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.toggle.setAttribute('aria-pressed', String(this.userPaused));
        this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
      });
      this.addEventListener('mouseenter', () => { this.hovered = true; });
      this.addEventListener('mouseleave', () => { this.hovered = false; });
      this.addEventListener('focusin', () => { this.hovered = true; });
      this.addEventListener('focusout', () => { this.hovered = false; });
      this.timer = setInterval(() => {
        if (this.userPaused || this.hovered || document.hidden) return;
        this.show((this.index + 1) % this.items.length);
      }, 5000);
    }
    show(next) {
      const current = this.items[this.index];
      current.setAttribute('aria-hidden', 'true');
      current.inert = true;
      this.index = next;
      this.items[next].setAttribute('aria-hidden', 'false');
      this.items[next].inert = false;
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Cart ---------- */
  const CartAPI = {
    sections() {
      const ids = ['cart-icon-bubble'];
      if (document.getElementById('shopify-section-cart-drawer') || qs('cart-drawer')) ids.push('cart-drawer');
      const main = qs('[data-main-cart]');
      if (main) ids.push(main.dataset.sectionId);
      return ids;
    },
    renderSections(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-icon-bubble') {
          qsa('[data-cart-bubble]').forEach((el) => { el.innerHTML = doc.querySelector('[data-cart-bubble-source]')?.innerHTML || ''; });
          return;
        }
        if (id === 'cart-drawer') {
          const current = qs('cart-drawer [data-cart-drawer-inner]');
          const next = doc.querySelector('[data-cart-drawer-inner]');
          if (current && next) current.innerHTML = next.innerHTML;
          return;
        }
        const target = document.getElementById(`shopify-section-${id}`);
        const source = doc.getElementById(`shopify-section-${id}`);
        if (target && source) target.innerHTML = source.innerHTML;
      });
    },
    async add(formData) {
      formData.append('sections', this.sections().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.renderSections(data.sections);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${theme.routes.cartChange}.js`, fetchConfig({ line, quantity, sections: this.sections(), sections_url: window.location.pathname }));
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      this.renderSections(data.sections);
      return data;
    },
    /**
     * Re-render the cart drawer, cart page and cart count from the server.
     * For apps that add to the cart themselves (e.g. product-option apps with price add-ons):
     * call `window.theme.cart.refresh({ open: true })` from the app's "after add to cart" script.
     */
    async refresh({ open = false } = {}) {
      // no-store: a cached response would show the cart as it was before the app added to it.
      const res = await fetch(`${theme.routes.cart}?sections=${this.sections().join(',')}`, { cache: 'no-store' });
      if (res.ok) this.renderSections(await res.json());
      if (open) this.openDrawer();
    },
    async note(note) {
      await fetch(`${theme.routes.cartUpdate}.js`, fetchConfig({ note }));
    },
    openDrawer(opener) {
      const drawer = qs('cart-drawer');
      if (drawer && theme.cartType === 'drawer') drawer.open(opener);
      else window.location.href = theme.routes.cart;
    }
  };
  window.theme.cart = CartAPI;
  // Same as above, for scripts that prefer dispatching an event: document.dispatchEvent(new Event('cart:refresh'))
  document.addEventListener('cart:refresh', () => CartAPI.refresh({ open: true }));

  class CartDrawer extends ThemeDrawer {}
  customElements.define('cart-drawer', CartDrawer);

  // Cart icon: open drawer instead of navigating
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-toggle]');
    if (!link || theme.cartType !== 'drawer' || !qs('cart-drawer')) return;
    e.preventDefault();
    CartAPI.openDrawer(link);
  });

  // Quantity & remove (works for drawer + cart page through delegation)
  const setLineLoading = (el, on) => el?.closest('[data-cart-line]')?.classList.toggle('is-loading-results', on);
  document.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('[data-qty-change]');
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (qtyBtn) {
      const input = qtyBtn.parentElement.querySelector('input');
      const next = Math.max(0, parseInt(input.value, 10) + parseInt(qtyBtn.dataset.qtyChange, 10));
      input.value = next;
      if (input.dataset.line) {
        setLineLoading(qtyBtn, true);
        try { await CartAPI.change(parseInt(input.dataset.line, 10), next); } catch (err) { toast(err.message); setLineLoading(qtyBtn, false); }
      } else {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (removeBtn) {
      e.preventDefault();
      setLineLoading(removeBtn, true);
      try { await CartAPI.change(parseInt(removeBtn.dataset.cartRemove, 10), 0); } catch (err) { toast(err.message); setLineLoading(removeBtn, false); }
    }
  });
  document.addEventListener('change', debounce(async (e) => {
    const input = e.target.closest('input[data-line]');
    if (!input) return;
    setLineLoading(input, true);
    try { await CartAPI.change(parseInt(input.dataset.line, 10), parseInt(input.value, 10) || 0); } catch (err) { toast(err.message); setLineLoading(input, false); }
  }, 400));
  document.addEventListener('input', debounce((e) => {
    const note = e.target.closest('[data-cart-note]');
    if (note) CartAPI.note(note.value);
  }, 500));

  /* ---------- Product form (PDP + quick add) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    // Product-option apps (e.g. Globo Product Options) validate first and cancel the submit
    // when a required option is missing; never add to cart behind their back.
    if (!form || e.defaultPrevented) return;
    e.preventDefault();

    // Bespoke configurator validation
    const configurator = form.closest('[data-product-root]')?.querySelector('bespoke-configurator') || qs(`bespoke-configurator[data-form-id="${form.id}"]`);
    if (configurator && !configurator.validate()) return;

    // Made-to-order confirmation (no withdrawal, return or exchange) must be ticked.
    const ack = qs('[data-made-to-order-ack]', form);
    const ackError = ack && qs('[data-ack-error]', ack.closest('.product-form__ack'));
    if (ack && !ack.checked) {
      if (ackError) ackError.hidden = false;
      ack.setAttribute('aria-invalid', 'true');
      ack.focus();
      ack.addEventListener('change', () => { if (ackError) ackError.hidden = ack.checked; ack.toggleAttribute('aria-invalid', !ack.checked); }, { once: true });
      return;
    }

    const button = qs('[type="submit"]', form);
    const errorEl = qs('[data-form-error]', form.closest('[data-product-root]') || form);
    if (errorEl) errorEl.hidden = true;
    button?.classList.add('is-loading');
    button?.setAttribute('aria-disabled', 'true');
    try {
      // Empty optional fields (e.g. notes) would otherwise reach the order as blank lines.
      const data = new FormData(form);
      [...data.entries()].forEach(([key, value]) => { if (key.startsWith('properties[') && typeof value === 'string' && !value.trim()) data.delete(key); });
      await CartAPI.add(data);
      // On the cart page the page itself was just re-rendered; the drawer is for everywhere else.
      if (!qs('[data-main-cart]')) CartAPI.openDrawer(button);
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; } else { toast(err.message); }
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-disabled');
    }
  });

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]') || document;
      const data = this.querySelector('script[data-variants]');
      this.variants = JSON.parse(data?.textContent || '[]');
      this.addEventListener('change', (e) => { if (e.target.closest('[data-option-index]')) this.onChange(); });
      // Liquid only lists the first 250 variants. Until the rest have loaded, nothing is marked unavailable,
      // so a leather chosen through the URL (e.g. ?variant= from Shop by Leather) is not cleared.
      const total = Number(data?.dataset.count || 0);
      if (total > this.variants.length && data?.dataset.handle) {
        this.loadAllVariants(data.dataset.handle).catch(() => {}).finally(() => {
          this.updateAvailability();
          if (!this.applyLeatherParam() && this.selectedOptions().every(Boolean)) this.onChange();
        });
      } else {
        this.updateAvailability();
        this.applyLeatherParam();
      }
    }
    // ?leather=Large+Grain+Alligator opens the configurator on that leather with the colour still to choose
    // (links from the homepage strap wall whose colour is not one of the listing's variants).
    applyLeatherParam() {
      const want = new URLSearchParams(window.location.search).get('leather');
      const [first, second] = this.groups();
      const input = want && first && qsa('input[type="radio"]', first).find((i) => i.value.toLowerCase() === want.toLowerCase());
      // Already chosen through ?variant= (a strap wall link with its colour): keep that colour.
      if (!input || input.checked) return false;
      input.checked = true;
      if (second) qsa('input:checked', second).forEach((i) => { i.checked = false; });
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    async loadAllVariants(handle) {
      const query = `query($h: String!, $a: String, $c: CountryCode, $l: LanguageCode) @inContext(country: $c, language: $l) {
        product(handle: $h) { variants(first: 250, after: $a) { pageInfo { hasNextPage endCursor }
          nodes { id availableForSale sku selectedOptions { value } price { amount } image { id } } } } }`;
      const country = window.Shopify?.country || null;
      const language = (window.Shopify?.locale || '').split('-')[0].toUpperCase() || null;
      const nodes = [];
      let after = null;
      do {
        const res = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, variables: { h: handle, a: after, c: country, l: language } }),
        });
        const page = (await res.json())?.data?.product?.variants;
        if (!page) return;
        nodes.push(...page.nodes);
        after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
      } while (after);
      const known = new Map(this.variants.map((v) => [v.id, v]));
      const ref = this.variants[0];
      const cents = (n) => Math.round(Number(n.price.amount) * 100);
      // Same presentment currency as the page, or keep the Liquid list as it is.
      const check = nodes.find((n) => known.has(Number(n.id.split('/').pop())));
      if (!ref || !check || cents(check) !== known.get(Number(check.id.split('/').pop())).price) return;
      const money = this.moneyFormatter(ref);
      nodes.forEach((n) => {
        const id = Number(n.id.split('/').pop());
        if (known.has(id)) return;
        const price = cents(n);
        this.variants.push({
          id, price, options: n.selectedOptions.map((o) => o.value), available: n.availableForSale, sku: n.sku || '',
          media_id: null, price_html: ref.price_html.replace(money(ref.price), money(price)),
        });
      });
      // Choices of the fetched leathers get their "from" price like the others.
      this.groups().forEach((g) => {
        const index = Number(g.dataset.optionIndex);
        qsa('input[type="radio"]', g).forEach((input) => {
          const label = qs('[data-price-label]', input.closest('.config-choice') || g);
          if (input.dataset.price || !qs('input[data-price]', g)) return;
          const prices = this.variants.filter((v) => v.options[index] === input.value).map((v) => v.price);
          if (!prices.length) return;
          const min = Math.min(...prices);
          input.dataset.price = min;
          if (!label && input.nextElementSibling) {
            const span = document.createElement('span');
            span.className = 'config-choice__desc';
            span.dataset.priceLabel = '';
            span.textContent = money(min).replace(/[.,]00(?!\d)/, '');
            (qs('.config-choice__text', input.nextElementSibling) || input.nextElementSibling).append(span);
          }
        });
      });
    }
    moneyFormatter(ref) {
      // Rebuild the shop's money format from a variant already formatted by Liquid (e.g. "$205.00", "179,00 €").
      const text = (ref.price_html.match(/class="price__current">([^<]+)</) || [])[1] || '';
      const m = text.match(/\d[\d.,\s]*\d|\d/);
      if (!m) return (c) => (c / 100).toFixed(2);
      const num = m[0];
      const dec = /[.,]\d{2}$/.test(num) ? num.slice(-3, -2) : '';
      const thou = dec === ',' ? '.' : ',';
      return (c) => {
        const whole = Math.floor(c / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, thou);
        const out = dec ? `${whole}${dec}${String(c % 100).padStart(2, '0')}` : whole;
        return text.replace(num, out);
      };
    }
    groups() {
      return qsa('[data-option-index]', this).sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));
    }
    selectedOptions() {
      return this.groups().map((g) => {
        const checked = qs('input:checked', g);
        if (checked) return checked.value;
        const select = qs('select', g);
        if (select) return select.value;
        return qs('input[type="hidden"]', g)?.value;
      });
    }
    onChange() {
      // First, as it may clear a stale choice or pick the only one left ("Colour on request") and run
      // onChange again from inside: the variant below must be read after that, not before.
      this.updateAvailability();
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.groups().forEach((g, i) => { const el = qs('[data-option-selected]', g); if (el) el.textContent = options[i] || ''; });
      if (!variant) {
        // Part-way through (e.g. a leather chosen, its colour not yet): show the price of the options picked so far.
        const partial = this.variants.filter((v) => v.options.every((o, i) => !options[i] || o === options[i]));
        if (partial.length && options.some(Boolean)) {
          const cheapest = partial.reduce((a, b) => (b.price < a.price ? b : a));
          qsa('[data-price]', this.root).forEach((el) => { el.innerHTML = cheapest.price_html; });
        }
        // Still choosing (not sold out): say so, or the customer reads the leather as unavailable.
        const choosing = partial.length && options.some((o) => !o);
        this.setButton(false, choosing ? theme.strings.chooseOptions || theme.strings.unavailable : theme.strings.unavailable);
        return;
      }
      this.currentVariant = variant;
      qsa('input[name="id"]', this.root).forEach((i) => { i.value = variant.id; });
      qsa('[data-price]', this.root).forEach((el) => { el.innerHTML = variant.price_html; });
      qsa('[data-sku]', this.root).forEach((el) => { el.textContent = variant.sku || ''; });
      this.setButton(variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      if (variant.media_id) this.root.dispatchEvent(new CustomEvent('variant:media', { detail: variant.media_id }));
      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    setButton(enabled, label) {
      qsa('[data-add-button]', this.root).forEach((btn) => {
        btn.disabled = !enabled;
        const text = qs('[data-add-label]', btn);
        if (text) text.textContent = label;
      });
    }
    updateAvailability() {
      const selected = this.selectedOptions();
      this.groups().forEach((g) => {
        const index = Number(g.dataset.optionIndex);
        qsa('input[type="radio"], option', g).forEach((input) => {
          const value = input.value;
          const available = this.variants.some((v) => v.available && v.options[index] === value &&
            v.options.every((o, i) => i >= index || o === selected[i]));
          if (input.tagName === 'OPTION') input.textContent = input.dataset.label + (available ? '' : ` — ${theme.strings.unavailable}`);
          else input.classList.toggle('is-unavailable', !available);
        });
      });
      // A choice that no longer exists for the earlier options (e.g. a colour of another leather) is cleared,
      // and when only one value remains (e.g. "Colour on request") it is chosen for the customer.
      this.groups().forEach((g) => {
        const stale = qs('input.is-unavailable:checked', g);
        if (stale) stale.checked = false;
        const open = qsa('input[type="radio"]:not(.is-unavailable)', g);
        const only = open.length === 1 && !qs('input:checked', g) ? open[0] : null;
        if (only) only.checked = true;
        if (stale || only) g.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('.product-gallery', this);
      this.items = qsa('.product-gallery__item', this);
      this.dots = qsa('.product-gallery__dot', this);
      if (this.dots.length) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              const i = this.items.indexOf(en.target);
              this.dots.forEach((d, di) => d.classList.toggle('is-active', di === i));
            }
          });
        }, { root: this.track, threshold: 0.6 });
        this.items.forEach((it) => io.observe(it));
      }
      this.thumbs = qsa('[data-thumb-index]', this);
      this.isSlider = this.track.classList.contains('product-gallery--slider');
      if (this.isSlider) {
        // Images inside the horizontal track are lazy; load the target and its neighbours before moving.
        const warm = (i) => [i - 1, i, i + 1, i + 2].forEach((n) => { const img = this.items[n] && qs('img', this.items[n]); if (img) img.loading = 'eager'; });
        const go = (i, dir = 0) => {
          let index = Math.max(0, Math.min(this.items.length - 1, i));
          // Step over photos hidden by the leather filter.
          while (dir && this.items[index]?.hidden && index > 0 && index < this.items.length - 1) index += dir;
          const item = this.items[index];
          if (!item || item.hidden) return;
          if (!item) return;
          warm(index);
          this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
        };
        const thumbBar = qs('.product-gallery__thumbs', this);
        this.current = 0;
        const sync = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            this.current = this.items.indexOf(en.target);
            warm(this.current);
            this.thumbs.forEach((t, ti) => {
              const on = ti === this.current;
              t.classList.toggle('is-active', on);
              // Keep the active thumbnail in view without scrolling the page itself.
              if (on && thumbBar) {
                const left = t.offsetLeft - thumbBar.offsetLeft;
                if (left < thumbBar.scrollLeft || left + t.offsetWidth > thumbBar.scrollLeft + thumbBar.clientWidth) {
                  thumbBar.scrollTo({ left: left - thumbBar.clientWidth / 2 + t.offsetWidth / 2, behavior: 'smooth' });
                }
              }
            });
          });
        }, { root: this.track, threshold: 0.6 });
        this.items.forEach((it) => sync.observe(it));
        this.thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.thumbIndex))));
        qs('[data-gallery-prev]', this)?.addEventListener('click', () => go(this.current - 1, -1));
        qs('[data-gallery-next]', this)?.addEventListener('click', () => go(this.current + 1, 1));
        this.go = go;
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); go(this.current + 1, 1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); go(this.current - 1, -1); }
        });
      }
      this.closest('[data-product-root]')?.addEventListener('variant:media', (e) => this.showMedia(e.detail));
      this.items.forEach((item, i) => item.addEventListener('click', () => this.openLightbox(i)));
    }
    // Show only the photos of one leather (matched against each photo's alt text); all photos when none match.
    filterBy(key) {
      if (key === this.filterKey) return;
      this.filterKey = key;
      const match = (el) => !key || (el.dataset.alt || '').includes(key);
      const any = key && this.items.some(match);
      this.activeKey = any ? key : '';
      const show = (el) => !any || match(el);
      this.items.forEach((it) => { it.hidden = !show(it); });
      this.thumbs.forEach((t) => { const it = this.items[Number(t.dataset.thumbIndex)]; t.hidden = !!it?.hidden; });
      this.dots.forEach((d, i) => { d.hidden = !!this.items[i]?.hidden; });
      const first = this.items.findIndex((it) => !it.hidden);
      if (first > -1) {
        if (this.go) this.go(first);
        else this.track.scrollTo({ left: 0 });
      }
    }
    showMedia(id) {
      const item = this.items.find((it) => it.dataset.mediaId === String(id));
      if (!item) return;
      if (window.matchMedia('(min-width: 990px)').matches && !this.isSlider) {
        if (this.track.firstElementChild !== item) this.track.prepend(item);
        this.items = qsa('.product-gallery__item', this);
      } else {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft - 16, behavior: 'smooth' });
      }
    }
    openLightbox(index) {
      const images = this.items.filter((it) => !it.hidden).map((it) => it.dataset.zoom).filter(Boolean);
      index = Math.max(0, this.items.filter((it) => !it.hidden).indexOf(this.items[index]));
      if (!images.length) return;
      let current = Math.min(index, images.length - 1);
      const box = document.createElement('div');
      box.className = 'lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.innerHTML = `<img alt=""><button class="lightbox__close" aria-label="Close">${ICON_CLOSE}</button>` +
        (images.length > 1 ? `<button class="lightbox__nav lightbox__nav--prev" aria-label="Previous">${ICON_PREV}</button><button class="lightbox__nav lightbox__nav--next" aria-label="Next">${ICON_NEXT}</button>` : '');
      const img = qs('img', box);
      const show = () => { img.src = images[current]; };
      const close = () => { box.remove(); document.body.classList.remove('overflow-hidden'); releaseFocus(); document.removeEventListener('keydown', onKey); };
      const onKey = (e) => {
        if (e.key === 'Escape') close();
        if (e.key === 'ArrowRight') { current = (current + 1) % images.length; show(); }
        if (e.key === 'ArrowLeft') { current = (current - 1 + images.length) % images.length; show(); }
      };
      qs('.lightbox__close', box).addEventListener('click', close);
      qs('.lightbox__nav--prev', box)?.addEventListener('click', () => { current = (current - 1 + images.length) % images.length; show(); });
      qs('.lightbox__nav--next', box)?.addEventListener('click', () => { current = (current + 1) % images.length; show(); });
      document.addEventListener('keydown', onKey);
      show();
      document.body.append(box);
      document.body.classList.add('overflow-hidden');
      trapFocus(box);
    }
  }
  const ICON_CLOSE = '<svg class="icon" viewBox="0 0 20 20" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16"/></svg>';
  const ICON_PREV = '<svg class="icon" viewBox="0 0 20 20" aria-hidden="true"><path d="M13 3L6 10l7 7"/></svg>';
  const ICON_NEXT = '<svg class="icon" viewBox="0 0 20 20" aria-hidden="true"><path d="M7 3l7 7-7 7"/></svg>';
  customElements.define('product-gallery', ProductGallery);

  /* ---------- Product info column: sticky, but never hiding its own bottom ----------
     A plain `top` offset keeps a column taller than the viewport pinned at its top, so the
     options and add-to-bag button below the fold stay out of reach until the gallery ends.
     When the column is taller than the viewport, stick it by its bottom edge instead. */
  const stickyColumns = qsa('.product__info-inner');
  if (stickyColumns.length) {
    const desktop = window.matchMedia('(min-width: 990px)');
    const place = () => stickyColumns.forEach((col) => {
      if (!desktop.matches) { col.style.top = ''; return; }
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0;
      const gap = 32;
      const overflow = col.offsetHeight + header + gap * 2 - window.innerHeight;
      col.style.top = `${overflow > 0 ? header + gap - overflow : header + gap}px`;
    });
    place();
    window.addEventListener('resize', debounce(place, 150));
    desktop.addEventListener('change', place);
    // Apps such as product-option forms render after load and change the column height.
    if ('ResizeObserver' in window) { const ro = new ResizeObserver(debounce(place, 100)); stickyColumns.forEach((c) => ro.observe(c)); }
  }

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target) return;
      // Show the bar once the product title has scrolled away, as long as the real button is off screen.
      const title = qs('.product__title', target.closest('[data-product-root]') || document);
      let titleGone = false;
      let buttonOnScreen = true;
      const update = () => {
        const visible = titleGone && !buttonOnScreen;
        this.classList.toggle('is-visible', visible);
        // Lets floating app widgets (chat buttons) move above the bar instead of covering its button.
        document.body.classList.toggle('has-sticky-atc', visible);
        document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
      };
      new IntersectionObserver(([en]) => { buttonOnScreen = en.isIntersecting; update(); }).observe(target);
      if (title) new IntersectionObserver(([en]) => { titleGone = !en.isIntersecting && en.boundingClientRect.top < 0; update(); }).observe(title);
      qs('button', this)?.addEventListener('click', () => {
        if (qs('bespoke-configurator')) { qs('bespoke-configurator').scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
        // Click the real button rather than submitting the form, so product-option apps
        // (e.g. Globo) run their required-field validation exactly as on the main button.
        const real = [...document.querySelectorAll(`#${CSS.escape(this.dataset.target)}, [form="${this.dataset.form}"][name="add"]`)].find((b) => b.offsetParent) || target;
        real.click();
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Bespoke configurator ---------- */
  class BespokeConfigurator extends HTMLElement {
    connectedCallback() {
      // A step set to "Show inside the previous step" (e.g. leather colour) moves under that step's choices,
      // so leathers and their colours can be browsed together without switching steps.
      qsa('.config-step[data-merge-previous]', this).forEach((step) => {
        let host = step.previousElementSibling;
        while (host && !host.classList.contains('config-step')) host = host.previousElementSibling;
        const panel = host && qs('.config-step__panel', host);
        if (!panel) return;
        const next = qs('[data-config-next]', panel);
        const block = document.createElement('div');
        block.className = 'config-nested';
        const title = document.createElement('p');
        title.className = 'config-subhead';
        title.textContent = step.dataset.label;
        block.append(title, step);
        panel.insertBefore(block, next);
        this.nested = this.nested || [];
        this.nested.push({ host, block, panel, next });
        step.classList.add('config-step--nested');
        qs('.config-step__panel', step).hidden = false;
        qs('[data-config-next]', step)?.remove();
        qs('.configurator__progress span', this)?.remove();
        // Its colour dot moves up next to the host step's summary.
        const dot = qs('[data-step-dot]', step);
        if (dot) { dot.source = step; qs('.config-step__selection', host).before(dot); }
      });
      this.steps = qsa('.config-step', this);
      this.top = this.steps.filter((s) => !s.classList.contains('config-step--nested'));
      this.top.forEach((s, i) => { const n = qs('.config-step__index', s); if (n) n.textContent = String(i + 1).padStart(2, '0'); });
      this.progress = qsa('.configurator__progress span', this);
      this.review = qs('[data-config-review]', this);
      qsa('[data-groups]', this).forEach((grid) => this.groupChoices(grid));
      this.lockLeather();
      this.placeNested();
      this.syncColourRequests();
      this.syncStitching();
      this.syncExtras();
      this.syncClaspSizes();
      // Panel layout: every short step stays open; only colour grids and the review fold open on demand.
      this.panel = this.classList.contains('configurator--panel');
      if (this.panel) {
        this.top.forEach((step) => {
          const fold = !!this.own(step, '.config-choices--swatch') || step.dataset.reviewable === 'false';
          if (fold) { step.classList.add('config-step--fold'); return; }
          const header = qs('.config-step__header', step);
          header.setAttribute('aria-expanded', 'true');
          header.tabIndex = -1;
          qs(':scope > .config-step__panel', step).hidden = false;
        });
      }
      this.top.forEach((step, i) => {
        qs('.config-step__header', step).addEventListener('click', () => this.openStep(i, true));
        qs(':scope > .config-step__panel > [data-config-next]', step)?.addEventListener('click', () => this.openStep(i + 1));
      });
      this.steps.forEach((step) => {
        step.addEventListener('change', (e) => { if (e.target.closest('.config-step') === step) this.onChange(step, this.top.indexOf(step), e); });
        qs('[data-colour-request] input', step)?.addEventListener('input', () => { this.updateStep(step); this.updateProgress(); this.renderReview(); });
        this.bindSwatchName(step);
        this.updateStep(step);
      });
      this.updateProgress();
    }
    openStep(index, toggle = false) {
      if (this.panel) {
        const step = this.top[index];
        if (!step) return;
        const header = qs('.config-step__header', step);
        if (step.classList.contains('config-step--fold')) {
          const open = !(toggle && header.getAttribute('aria-expanded') === 'true');
          header.setAttribute('aria-expanded', String(open));
          qs(':scope > .config-step__panel', step).hidden = !open;
        } else if (toggle) return;
        if (!toggle) step.scrollIntoView({ block: 'center', behavior: 'smooth' });
        this.renderReview();
        return;
      }
      this.top.forEach((s, i) => {
        const header = qs('.config-step__header', s);
        const panel = qs('.config-step__panel', s);
        const open = i === index && !(toggle && header.getAttribute('aria-expanded') === 'true');
        header.setAttribute('aria-expanded', String(open));
        panel.hidden = !open;
      });
      const step = this.top[index];
      if (step && !toggle) {
        const top = step.getBoundingClientRect().top + window.scrollY - 120;
        if (step.getBoundingClientRect().top < 80 || step.getBoundingClientRect().top > window.innerHeight * 0.7) window.scrollTo({ top, behavior: 'smooth' });
      }
      this.renderReview();
    }
    // The first match that belongs to this step itself, not to a step nested inside it.
    own(step, selector) { return qsa(selector, step).find((el) => el.closest('.config-step') === step); }
    valueOf(step) {
      const request = this.own(step, '[data-colour-request]:not([hidden]) input');
      if (request) return request.value.trim();
      const checked = this.own(step, 'input:checked');
      if (checked) return checked.dataset.label || checked.value;
      const field = this.own(step, 'select, input[type="text"], textarea');
      return field ? field.value.trim() : '';
    }
    updateStep(step) {
      const own = this.valueOf(step);
      const nested = qsa('.config-step--nested', step).filter((n) => n.parentElement.closest('.config-step') === step);
      const parts = [own, ...nested.map((n) => this.valueOf(n))];
      const value = nested.length ? (parts.every(Boolean) ? parts.join(' · ') : '') : own;
      const sel = this.own(step, '.config-step__selection');
      const offText = step.classList.contains('is-off') ? theme.strings.stitchingNotNeeded : '';
      if (sel) sel.textContent = offText || value || (nested.length && own ? own : '') || sel.dataset.placeholder || '';
      step.classList.toggle('is-complete', !!value || !!offText);
      nested.forEach((n) => this.updateStep(n));
      // Colour steps echo the chosen sample next to its name, so closed steps still read at a glance.
      const dot = this.own(step, '[data-step-dot]');
      if (dot) {
        const target = dot.source || step;
        const swatch = this.own(target, 'input:checked + label .config-choice__swatch');
        const onRequest = this.own(target, '[data-colour-request]:not([hidden])');
        const image = onRequest ? '' : swatch?.style.getPropertyValue('--swatch');
        if (image) dot.style.setProperty('--swatch', image);
        dot.hidden = !image;
      }
      const name = this.own(step, '[data-swatch-name]');
      if (name) name.textContent = own;
    }
    bindSwatchName(step) {
      const name = qs('[data-swatch-name]', step);
      if (!name) return;
      const show = (e) => {
        const label = e.target.closest('.config-choice')?.querySelector('input')?.dataset.label;
        if (label) name.textContent = label;
      };
      const reset = () => { name.textContent = this.valueOf(step); };
      const grid = qs('.config-choices--swatch', step);
      grid?.addEventListener('pointerover', (e) => { if (e.pointerType === 'mouse') show(e); });
      grid?.addEventListener('focusin', show);
      grid?.addEventListener('pointerleave', reset);
      grid?.addEventListener('focusout', reset);
    }
    // Padding and quick-release spring bars carry a surcharge, so together they pick the product's "Extras" option.
    syncExtras() {
      const field = qs('[data-extras] input', this);
      if (!field) return;
      const padding = this.valueOf(qs('[data-preview-role="padding"]', this) || this);
      const springBar = this.valueOf(qs('[data-preview-role="spring_bar"]', this) || this);
      const padded = /^(padded|gepolstert)/i.test(padding); const quick = /quick|schnellwechsel/i.test(springBar);
      const value = padded && quick ? 'Padded + Quick-release' : padded ? 'Padded' : quick ? 'Quick-release' : 'Standard';
      if (field.value === value) return;
      field.value = value;
      qs('variant-picker', this)?.onChange();
    }
    // "No stitch" leaves nothing to colour: the thread colour step is switched off and no longer required.
    syncStitching() {
      const style = qs('.config-step[data-preview-role="stitch_style"]', this);
      const colour = qs('.config-step[data-preview-role="stitching"]', this);
      if (!style || !colour) return;
      if (colour.dataset.requiredDefault === undefined) colour.dataset.requiredDefault = colour.dataset.required;
      const off = /^(no\b|ohne|keine)/i.test(this.valueOf(style));
      colour.classList.toggle('is-off', off);
      colour.dataset.required = off ? 'false' : colour.dataset.requiredDefault;
      qsa('input, select, textarea', colour).forEach((input) => {
        if (off && input.checked) input.checked = false;
        input.disabled = off;
      });
    }
    // Own-clasp models: a butterfly clasp takes the equal 120/120 length only; a deployant clasp takes the others.
    syncClaspSizes() {
      const clasp = qs('.config-step[data-preview-role="clasp"]', this);
      const select = qs('.config-step[data-preview-role="strap_size"] select', this);
      if (!clasp || !select) return;
      const value = this.valueOf(clasp);
      if (!value) { if (select.value && !/\b(\d{2,3})\s*\/\s*\1\b/.test(select.value)) this.deployantSize = select.value; return; }
      const butterfly = /butterfly|schmetterling/i.test(value);
      const fits = [...select.options].filter((o) => {
        const ok = !o.value || /\b(\d{2,3})\s*\/\s*\1\b/.test(o.value) === butterfly;
        o.hidden = !ok; o.disabled = !ok;
        return ok && o.value;
      });
      const keep = fits.some((o) => o.value === select.value);
      // Remember a deployant length, so switching butterfly → deployant brings it back.
      if (!butterfly && keep && select.value) this.deployantSize = select.value;
      const back = !butterfly && fits.some((o) => o.value === this.deployantSize) ? this.deployantSize : '';
      const next = keep ? select.value : (back || (fits.length === 1 ? fits[0].value : ''));
      if (next !== select.value) { select.value = next; select.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    onChange(step, i, e) {
      this.syncColourRequests();
      this.syncStitching();
      this.syncExtras();
      this.syncClaspSizes();
      this.syncFamilies();
      this.placeNested();
      this.steps.forEach((s) => this.updateStep(s));
      this.updateProgress();
      this.renderReview();
      // Only a customer's own choice moves the flow on; choices made for them (e.g. a single colour) do not.
      const waiting = qs('[data-colour-request]:not([hidden])', step);
      if (i > -1 && e?.isTrusted && !waiting && step.dataset.autoAdvance === 'true' && qs('input[type="radio"]:checked', step)) {
        setTimeout(() => this.openStep(i + 1), 250);
      }
    }
    // Nested choices (leather colours) sit directly under the chosen leather row, or under the Alligator finishes.
    placeNested() {
      (this.nested || []).forEach(({ host, block, panel, next }) => {
        const checked = this.own(host, 'input[type="radio"]:checked');
        const row = checked && (checked.closest('.config-family__finishes') || checked.closest('.config-choice'));
        const target = row && !row.closest('[hidden]') ? row : null;
        if (target) { if (target.nextElementSibling !== block) target.after(block); }
        else if (block.nextElementSibling !== next) panel.insertBefore(block, next);
      });
    }
    syncColourRequests() {
      qsa('[data-colour-request]', this).forEach((box) => {
        const checked = qs('input[type="radio"]:checked', box.closest('.config-step'));
        const show = checked?.value === 'Colour on request';
        const input = qs('input', box);
        box.hidden = !show;
        input.disabled = !show;
        input.required = show;
      });
    }
    // Turns a flat list of leathers into groups (Traditional / Exotic) with families (Alligator: three finishes).
    groupChoices(grid) {
      const byValue = new Map(qsa('.config-choice', grid).map((c) => [qs('input', c).value, c]));
      const groups = grid.dataset.groups.split('\n').map((line) => {
        const at = line.indexOf(':');
        if (at < 0) return null;
        const items = [...line.slice(at + 1).matchAll(/\s*([^,(]+?)\s*(?:\(([^)]*)\))?\s*(?:,|$)/g)]
          .filter((m) => m[1])
          .map((m) => ({ name: m[1].trim(), children: m[2] ? m[2].split(',').map((v) => v.trim()).filter((v) => byValue.has(v)) : null }))
          .filter((it) => (it.children ? it.children.length : byValue.has(it.name)));
        return items.length ? { name: line.slice(0, at).trim(), items } : null;
      }).filter(Boolean);
      if (groups.length === 0) return;
      const uid = grid.closest('.config-step__panel')?.id || 'config';
      const segment = document.createElement('div');
      segment.className = 'config-segment';
      grid.classList.add('is-grouped');
      grid.before(segment);
      this.families = this.families || [];
      groups.forEach((group, gi) => {
        const panel = document.createElement('div');
        panel.className = 'config-group';
        panel.id = `${uid}-group-${gi}`;
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.textContent = group.name;
        tab.setAttribute('aria-controls', panel.id);
        tab.addEventListener('click', () => this.showGroup(segment, tab));
        segment.append(tab);
        group.items.forEach((item) => {
          if (!item.children) { panel.append(byValue.get(item.name)); return; }
          const box = document.createElement('div');
          box.className = 'config-family__finishes';
          box.id = `${panel.id}-${item.name.toLowerCase().replace(/\W+/g, '-')}`;
          box.hidden = true;
          const note = document.createElement('p');
          note.className = 'config-family__note';
          note.textContent = grid.dataset.finishLabel || '';
          box.append(note);
          let min = null;
          item.children.forEach((value) => {
            const choice = byValue.get(value);
            const name = qs('.config-choice__name', choice);
            if (name) name.textContent = value.replace(new RegExp(`\\s*${item.name}\\s*`), ' ').trim();
            const price = qs('[data-price-label]', choice);
            const cents = Number(qs('input', choice).dataset.price);
            if (price && (min === null || cents < min.cents)) min = { cents, text: price.textContent };
            choice.classList.add('config-choice--finish');
            box.append(choice);
          });
          const card = document.createElement('button');
          card.type = 'button';
          card.className = 'config-family';
          card.setAttribute('aria-controls', box.id);
          card.setAttribute('aria-expanded', 'false');
          card.innerHTML = `<span class="config-choice__text"><span class="config-choice__name"></span>${min ? '<span class="config-choice__desc"></span>' : ''}</span>`;
          const icon = qs('.config-choice__icon', byValue.get(item.children[0]));
          if (icon) card.prepend(icon.cloneNode(true));
          qs('.config-choice__name', card).textContent = item.name;
          if (min) qs('.config-choice__desc', card).textContent = `${grid.dataset.fromLabel || ''} ${min.text}`.trim();
          card.addEventListener('click', () => { box.hidden = false; card.setAttribute('aria-expanded', 'true'); });
          // The finishes open directly beneath their family row.
          panel.append(card, box);
          this.families.push({ card, box });
        });
        grid.append(panel);
      });
      const checked = qs('input:checked', grid);
      const start = qsa('button', segment)[Math.max(0, qsa('.config-group', grid).findIndex((p) => checked && p.contains(checked)))];
      this.showGroup(segment, start);
      this.syncFamilies();
    }
    // Arriving from a leather (?leather=…: Shop by Leather, the strap wall) shows only that leather, so the
    // customer is not asked to choose again; "Choose another leather" brings every leather back.
    lockLeather() {
      const want = new URLSearchParams(window.location.search).get('leather');
      if (!want) return;
      const input = qsa('input[type="radio"]', this).find((i) => i.value.toLowerCase() === want.toLowerCase());
      const step = input?.closest('.config-step');
      const choice = input?.closest('.config-choice');
      if (!step || !choice) return;
      step.classList.add('is-leather-locked');
      choice.classList.add('is-leather-chosen');
      // Inside its family the name is shortened ("Matte Flank"); alone it needs the full name.
      const name = qs('.config-choice__name', choice);
      const short = name?.textContent;
      if (name) name.textContent = input.dataset.label || input.value;
      for (let el = choice.parentElement; el && el !== step; el = el.parentElement) el.hidden = false;
      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'config-leather-change';
      change.textContent = theme.strings.otherLeather || 'Choose another leather';
      change.addEventListener('click', () => {
        step.classList.remove('is-leather-locked');
        if (name) name.textContent = short;
        change.remove();
        const group = choice.closest('.config-group');
        const tab = group && qs(`.config-segment [aria-controls="${group.id}"]`, step);
        if (tab) this.showGroup(tab.parentElement, tab);
        this.syncFamilies();
      });
      (choice.closest('[data-groups], .config-choices') || choice).after(change);
    }
    showGroup(segment, tab) {
      qsa('button', segment).forEach((b) => {
        const on = b === tab;
        b.setAttribute('aria-pressed', String(on));
        document.getElementById(b.getAttribute('aria-controls')).hidden = !on;
      });
      if (this.nested) this.placeNested();
    }
    syncFamilies() {
      (this.families || []).forEach(({ card, box }) => {
        const chosen = !!qs('input:checked', box);
        const sibling = qs('input:checked', card.parentElement) && !chosen;
        card.classList.toggle('is-active', chosen);
        if (chosen) box.hidden = false;
        if (sibling) box.hidden = true;
        card.setAttribute('aria-expanded', String(!box.hidden));
      });
    }
    updateProgress() {
      this.top.forEach((s, i) => this.progress[i]?.classList.toggle('is-done', s.classList.contains('is-complete')));
    }
    renderReview() {
      if (!this.review) return;
      const rows = this.steps.filter((s) => s.dataset.reviewable !== 'false').map((s) => {
        const label = s.dataset.label;
        const value = this.valueOf(s);
        const required = s.dataset.required === 'true';
        // Chosen samples (leather, lining, thread, buckle) show their swatch beside the name.
        const swatch = value && !qs('[data-colour-request]:not([hidden])', s) ? qs('input:checked + label .config-choice__swatch', s)?.style.getPropertyValue('--swatch') : '';
        const dot = swatch ? `<span class="config-review__dot" style="--swatch: ${swatch.replace(/"/g, '&quot;')}"></span>` : '';
        const empty = s.classList.contains('is-off') ? theme.strings.stitchingNotNeeded : '—';
        return `<dt>${label}</dt><dd class="${!value && required ? 'is-missing' : ''}">${dot}${value ? value.replace(/</g, '&lt;') : (required ? this.dataset.requiredText : empty)}</dd>`;
      });
      this.review.innerHTML = rows.join('');
    }
    validate() {
      const missing = this.steps.findIndex((s) => s.dataset.required === 'true' && !this.valueOf(s));
      if (missing > -1) {
        this.openStep(missing);
        toast(this.dataset.incompleteText || 'Please complete every step.');
        return false;
      }
      return true;
    }
  }
  customElements.define('bespoke-configurator', BespokeConfigurator);
  /* ---------- Strap preview: a live drawing of the strap being configured ---------- */
  class StrapPreview extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]') || document;
      this.uid = qs('pattern', this).id.replace(/-lining$/, '');
      this.gallery = qs('product-gallery', this.root);
      this.side = 'front';
      this.threads = new Map();
      qsa('[data-preview-view]', this).forEach((b) => b.addEventListener('click', () => { this.viewChosen = true; this.setView(b.dataset.previewView); }));
      qsa('[data-preview-side]', this).forEach((b) => b.addEventListener('click', () => { this.autoSide = false; this.setSide(b.dataset.previewSide); }));
      // With product photos the page opens on them; the drawing takes over at the customer's first choice.
      const hasPhotos = !!this.gallery && !!qs('.product-gallery__item[data-media-id]', this.gallery);
      this.setView(hasPhotos ? 'photos' : 'preview');
      const configurator = qs('bespoke-configurator', this.root);
      configurator?.addEventListener('change', (e) => {
        if (!e.isTrusted) return;
        // A colour choice always shows the drawing, even after the customer opened the photos.
        const role = e.target.closest?.('.config-step')?.dataset.previewRole || '';
        if (!this.viewChosen || /colour|lining|stitching/.test(role)) { this.viewChosen = true; this.setView('preview'); }
      });
      // Draw after the configurator has settled its own state for this change.
      ['change', 'input'].forEach((type) => configurator?.addEventListener(type, () => requestAnimationFrame(() => this.render())));
      // While the spring bar step is open the strap turns over, so the bars at the lug ends can be seen.
      const springPanel = qs('.config-step__panel', this.step('spring_bar') || document.createElement('div'));
      if (configurator?.classList.contains('configurator--panel')) {
        // Panel layout keeps every step open, so the side follows where the customer is working:
        // lining colour and spring bars turn the strap over, any other option turns it back.
        const follow = (e) => {
          const step = e.target.closest?.('.config-step');
          if (!step || step.dataset.reviewable === 'false') return;
          const back = ['lining', 'spring_bar'].includes(step.dataset.previewRole);
          if (back && (this.side !== 'lining' || !this.partial)) { this.autoSide = true; this.setSide('lining', true); }
          else if (!back && this.autoSide) { this.autoSide = false; this.setSide('front'); }
        };
        ['pointerdown', 'focusin', 'change'].forEach((type) => configurator.addEventListener(type, follow));
      } else if (springPanel) {
        new MutationObserver(() => {
          if (!springPanel.hidden) { this.autoSide = this.side !== 'lining'; this.setSide('lining', true); }
          else if (this.autoSide) { this.autoSide = false; this.setSide('front'); }
        }).observe(springPanel, { attributes: true, attributeFilter: ['hidden'] });
      }
      this.render();
    }
    // Leather buttons beside the tabs: one per leather found in the photo alt texts; a tap opens its photos.
    renderLeathers() {
      const bar = qs('[data-preview-leathers]', this);
      if (!bar || !this.gallery) return;
      if (!bar.dataset.built) {
        bar.dataset.built = '1';
        const keys = [];
        qsa('.product-gallery__item[data-alt]', this.gallery).forEach((it) => {
          const k = (it.dataset.alt.match(/^(.*?) leather strap/) || [])[1];
          if (k && !keys.includes(k)) keys.push(k);
        });
        if (keys.length < 2) { bar.hidden = true; return; }
        const label = (k) => k.replace(/\b\w/g, (ch) => ch.toUpperCase());
        bar.innerHTML = [['', bar.dataset.allLabel || 'All'], ...keys.map((k) => [k, label(k)])]
          .map(([k, l]) => `<button type="button" data-leather-key="${k}" aria-pressed="false">${l}</button>`).join('');
        qsa('button', bar).forEach((b) => b.addEventListener('click', () => {
          this.viewChosen = true;
          this.gallery.filterBy(b.dataset.leatherKey);
          this.setView('photos');
        }));
        bar.hidden = false;
      }
      const active = this.gallery.activeKey || '';
      qsa('button', bar).forEach((b) => {
        const k = b.dataset.leatherKey;
        b.setAttribute('aria-pressed', String(k === active || (!!k && !!active && (k.includes(active) || active.includes(k)))));
      });
    }
    setView(view) {
      this.view = view;
      qsa('[data-preview-view]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.previewView === view)));
      qs('[data-preview-stage]', this).hidden = view !== 'preview';
      if (this.gallery) this.gallery.hidden = view === 'preview';
      this.renderLeathers();
    }
    // partial: only the long piece turns over (while choosing the lining or spring bars); the Lining button turns both.
    setSide(side, partial = false) {
      this.side = side;
      this.partial = side === 'lining' && partial;
      qsa('[data-preview-side]', this).forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.previewSide === side)));
      this.render();
    }
    step(role) { return qs(`.config-step[data-preview-role="${role}"]`, this.root); }
    // The short description of the chosen choice in a step, e.g. "18 mm at the clasp".
    choiceNote(role) {
      const step = this.step(role);
      const checked = step && this.own(step, 'input[type="radio"]:checked');
      return checked?.closest('.config-choice')?.querySelector('.config-choice__desc')?.textContent || '';
    }
    own(step, selector) { return qsa(selector, step).find((el) => el.closest('.config-step') === step); }
    value(role) {
      const step = this.step(role);
      if (!step) return '';
      const request = this.own(step, '[data-colour-request]:not([hidden]) input');
      if (request) return request.value.trim();
      const checked = this.own(step, 'input[type="radio"]:checked');
      if (checked) return checked.dataset.label || checked.value;
      const field = this.own(step, 'select, input[type="text"]');
      return field ? field.value.trim() : '';
    }
    photo(role, width) {
      const step = this.step(role);
      if (!step || this.own(step, '[data-colour-request]:not([hidden])')) return '';
      const swatch = this.own(step, 'input[type="radio"]:checked + label .config-choice__swatch');
      const match = (swatch?.style.getPropertyValue('--swatch') || '').match(/url\(['"]?(.*?)['"]?\)/);
      if (!match) return '';
      const url = match[1].replace(/([?&])width=\d+/, `$1width=${width}`);
      return url.startsWith('//') ? location.protocol + url : url;
    }
    // Average colour of the middle of a thread photo, so the stitches carry the real thread tone.
    threadColour(url) {
      if (!url) return Promise.resolve(null);
      if (!this.threads.has(url)) {
        this.threads.set(url, new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const c = document.createElement('canvas');
              c.width = c.height = 12;
              const ctx = c.getContext('2d');
              ctx.drawImage(img, img.naturalWidth * 0.3, img.naturalHeight * 0.3, img.naturalWidth * 0.4, img.naturalHeight * 0.4, 0, 0, 12, 12);
              const d = ctx.getImageData(0, 0, 12, 12).data;
              let r = 0; let g = 0; let b = 0;
              for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; }
              const n = d.length / 4;
              resolve(`rgb(${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)})`);
            } catch (e) { resolve(null); }
          };
          img.onerror = () => resolve(null);
          img.src = url;
        }));
      }
      return this.threads.get(url);
    }
    // A square tile from the middle of a sample photo, every second row mirrored so joins continue the grain.
    texture(id, url, size, stretch, x, y, base) {
      const H = size * stretch;
      const tile = url ? `<svg width="${size}" height="${H}" overflow="hidden"><image href="${url}" x="${-size * 0.12}" y="${-H * 0.12}" width="${size * 1.24}" height="${H * 1.24}" preserveAspectRatio="none"/></svg>` : '';
      return `<pattern id="${id}" patternUnits="userSpaceOnUse" x="${x}" y="${y}" width="${size}" height="${H * 2}"><rect width="${size}" height="${H * 2}" fill="${base}"/>${tile}${tile ? `<g transform="translate(0 ${H * 2}) scale(1 -1)">${tile}</g>` : ''}</pattern>`;
    }
    // French-style saddle stitching: one short slanted stitch per prick of the iron, following the seam line.
    stitches(seam, keep) {
      if (!this.measure) {
        this.measure = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        this.measure.setAttribute('fill', 'none');
        this.measure.style.visibility = 'hidden';
        qs('svg', this).append(this.measure);
      }
      this.measure.setAttribute('d', seam);
      const total = this.measure.getTotalLength();
      const pitch = 3 * 3.4; const half = 2.8; const tilt = 0.7; // one stitch every 3 mm, ~40° to the seam
      let d = '';
      for (let s = pitch / 2; s < total; s += pitch) {
        const a = this.measure.getPointAtLength(Math.max(0, s - 0.5));
        const b = this.measure.getPointAtLength(Math.min(total, s + 0.5));
        const p = this.measure.getPointAtLength(s);
        if (keep && !keep(p)) continue;
        const angle = Math.atan2(b.y - a.y, b.x - a.x) + tilt;
        const dx = Math.cos(angle) * half; const dy = Math.sin(angle) * half;
        d += `M${(p.x - dx).toFixed(1)} ${(p.y - dy).toFixed(1)}L${(p.x + dx).toFixed(1)} ${(p.y + dy).toFixed(1)}`;
      }
      return d;
    }
    render() {
      const k = 3.4; // drawing pixels per millimetre
      const lugText = this.value('lug'); // "20 mm" or "Large (21 mm)"
      // Millimetres may carry a decimal comma on the German site ("17,8 mm").
      const mm = (s) => parseFloat(((String(s).match(/(\d+(?:[.,]\d+)?)\s*mm/) || [])[1] || String(s)).replace(',', '.'));
      const lug = mm(lugText) || 20;
      const lugEnd = mm(this.choiceNote('lug'));
      const buckleMm = Math.min(mm(this.value('buckle_width')) || lugEnd || Math.max(lug - 2, 10), lug);
      // Photos follow the chosen leather.
      const lk = this.value('leather').toLowerCase();
      const leatherKey = /large grain/.test(lk) ? 'large grain' : /matte/.test(lk) ? 'matte alligator' : /shiny/.test(lk) ? 'shiny alligator' : /ostrich/.test(lk) ? 'ostrich' : /goat/.test(lk) ? 'goat' : /lizard/.test(lk) ? 'lizard' : /stingray/.test(lk) ? 'stingray' : /calf/.test(lk) ? 'calf' : /alligator/.test(lk) ? 'alligator' : '';
      if (leatherKey !== this.lastLeatherKey) { this.lastLeatherKey = leatherKey; this.gallery?.filterBy?.(leatherKey); }
      this.renderLeathers();
      const size = this.value('strap_size').match(/(\d{2,3})\s*\/\s*(\d{2,3})/);
      const equal = this.dataset.clasp === 'butterfly' || (!!this.step('clasp') && /butterfly|schmetterling/i.test(this.value('clasp')));
      const longMm = size ? Number(size[1]) : (equal ? 120 : 115);
      const shortMm = size ? Number(size[2]) : (equal ? 120 : 75);
      const W = lug * k;
      const w = buckleMm * k;
      const y0 = 70;
      const u = this.uid;
      const lining = this.side === 'lining';
      // Without a buckle step, a model worn on its own tang buckle (clasp step) shows a plain steel buckle.
      const buckle = this.value('buckle') || (!this.step('buckle') && /standard|tang|dorn/i.test(this.value('clasp')) ? 'Silver' : '');
      const style = this.value('stitch_style');
      const padValue = this.value('padding');
      const padded = /^(padded|gepolstert)/i.test(padValue);
      const quick = /quick|schnellwechsel/i.test(this.value('spring_bar'));
      const leatherUrl = this.photo('leather_colour', 900);
      const liningUrl = this.photo('lining', 600);
      const gold = /gold/i.test(buckle);
      const metal = gold ? ['#f7e3a3', '#c8973c', '#f0d48c', '#9c7024', '#e9c877'] : ['#f6f6f4', '#b3b3b0', '#ecece9', '#8a8a87', '#d9d9d6'];
      const liningImg = qs('[data-preview-lining]', this);
      liningImg.setAttribute('href', liningUrl);
      liningImg.style.display = liningUrl ? '' : 'none';

      // Long piece on the left (holes, pointed tip); short piece on the right (keepers, buckle). Both taper lug → buckle.
      // Models worn on their own clasp (a "clasp" step, e.g. Cartier Santos) have no keepers or buckle and show the
      // QuickSwitch bar ends at the lug. Deployant clasp: long piece with holes, short piece cut square and stitched round,
      // its pin ends showing at the sides. Butterfly clasp: two equal pointed pieces without holes.
      // A model made for one clasp only (data-clasp="butterfly", e.g. Santos 100) needs no clasp step.
      // data-lug-fitting="pin": attached by a pin through its own end piece, not QuickSwitch.
      // A clasp step can also offer the watch's own tang buckle ("For Standard Buckle", e.g. Santos-Dumont):
      // then the strap is drawn as a buckle strap (holes, keepers, spring bars).
      const tang = /standard|tang|dorn/i.test(this.value('clasp'));
      const claspStep = (!!this.step('clasp') && !tang) || this.dataset.clasp === 'butterfly';
      const butterfly = this.dataset.clasp === 'butterfly' || (!!claspStep && /butterfly|schmetterling/i.test(this.value('clasp')));
      // data-lug-fitting="adapter": a metal end piece behind the strap end with a quick-release bar through it (e.g. Santos-Dumont).
      const adapter = this.dataset.lugFitting === 'adapter';
      const quickSwitch = !!claspStep && !['pin', 'adapter'].includes(this.dataset.lugFitting);
      const pieces = butterfly
        ? [{ cx: 168, len: longMm * k, tip: true }, { cx: 318, len: shortMm * k, tip: true }]
        : [{ cx: 168, len: longMm * k, tip: true, holes: !(claspStep && /no holes|ohne löcher/i.test(this.value('clasp'))) }, { cx: 318, len: shortMm * k, keepers: !claspStep, pin: !!claspStep }];
      // Each piece narrows from the lug to the buckle width over its first 5 cm, then runs straight at the buckle width.
      const taperLen = (p) => Math.min(50 * k, p.len * 0.85);
      const widthAt = (p, y) => W + (w - W) * Math.min(1, Math.max(0, (y - y0) / taperLen(p)));
      const outline = (p, inset = 0) => {
        const y1 = y0 + p.len - inset;
        const a = W / 2 - inset; const b = w / 2 - inset; const top = y0 + (inset ? inset + 4 : 0);
        const bend = y0 + taperLen(p);
        if (p.tip) {
          const shoulder = Math.max(y1 - b * 1.25, bend);
          const path = `M${p.cx - a} ${top}L${p.cx - b} ${bend}L${p.cx - b} ${shoulder}Q${p.cx - b} ${y1 - b * 0.35} ${p.cx} ${y1}Q${p.cx + b} ${y1 - b * 0.35} ${p.cx + b} ${shoulder}L${p.cx + b} ${bend}L${p.cx + a} ${top}`;
          return inset ? path : `${path}Z`;
        }
        return inset ? `M${p.cx - a} ${top}L${p.cx - b} ${bend}L${p.cx - b} ${y1}H${p.cx + b}L${p.cx + b} ${bend}L${p.cx + a} ${top}` : `M${p.cx - a} ${y0}H${p.cx + a}L${p.cx + b} ${bend}L${p.cx + b} ${y1}H${p.cx - b}L${p.cx - b} ${bend}Z`;
      };
      const edge = 'rgb(30 20 12 / 0.38)';
      // Padding rises over the first 4 cm from the lug, then eases flat.
      const padH = 40 * k;
      let defs = `<linearGradient id="${u}-pad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stop-color="#000" stop-opacity=".36"/>
        <stop offset=".18" stop-color="#000" stop-opacity=".06"/>
        <stop offset=".42" stop-color="#fff" stop-opacity=".24"/>
        <stop offset=".7" stop-color="#fff" stop-opacity="0"/>
        <stop offset="1" stop-color="#000" stop-opacity=".4"/>
      </linearGradient>
      <linearGradient id="${u}-padfade" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff"/><stop offset=".72" stop-color="#fff"/><stop offset="1" stop-color="#000"/></linearGradient>
      <mask id="${u}-padmask" maskUnits="userSpaceOnUse" x="0" y="0" width="480" height="620"><rect x="0" y="${y0}" width="480" height="${padH}" fill="url(#${u}-padfade)"/></mask>`;
      let svg = '';
      const threadFor = [];
      pieces.forEach((p, pi) => {
        const lining = this.side === 'lining' && (!this.partial || pi === 0);
        const d = outline(p);
        const y1 = y0 + p.len;
        const fillId = `${u}-tex-${pi}`;
        defs += lining
          ? this.texture(fillId, liningUrl, 130, 1, p.cx - 65, y0, '#d8c2a2')
          : this.texture(fillId, leatherUrl, 195, 1.3, p.cx - 97.5, y0, '#7a5a44');
        if (p.keepers && buckle && !/no buckle|ohne schlie/i.test(buckle)) {
          // Tang buckle as solid metal: slim sides, a broader curved base, a bar under the strap and a tapered tongue.
          const bw = w + 18; const l = p.cx - bw / 2; const r = p.cx + bw / 2; const yb = y1 - 5; const h = bw * 0.6;
          const R = h * 0.5; const ts = 3.6; const tb = 7; const R2 = Math.max(R - 4, 2);
          defs += `<linearGradient id="${u}-buckle" gradientUnits="userSpaceOnUse" x1="${l}" y1="${yb}" x2="${r}" y2="${yb + h}">${metal.map((c, i) => `<stop offset="${(i / (metal.length - 1)).toFixed(2)}" stop-color="${c}"/>`).join('')}</linearGradient>`;
          const frame = `M${l} ${yb}V${yb + h - R}Q${l} ${yb + h} ${l + R} ${yb + h}H${r - R}Q${r} ${yb + h} ${r} ${yb + h - R}V${yb}H${r - ts}V${yb + h - tb - R2}Q${r - ts} ${yb + h - tb} ${r - ts - R2} ${yb + h - tb}H${l + ts + R2}Q${l + ts} ${yb + h - tb} ${l + ts} ${yb + h - tb - R2}V${yb}Z`;
          svg += `<path d="${frame}" fill="url(#${u}-buckle)" stroke="rgb(0 0 0 / 0.18)" stroke-width="0.6"/>`;
          svg += `<rect x="${l - 2.5}" y="${yb - 2.6}" width="${bw + 5}" height="5.2" rx="2.6" fill="url(#${u}-buckle)" stroke="rgb(0 0 0 / 0.18)" stroke-width="0.6"/>`;
          p.tongue = `<path d="M${p.cx - 3.2} ${yb - 7}L${p.cx - 1.6} ${yb + h - 4}Q${p.cx} ${yb + h - 1} ${p.cx + 1.6} ${yb + h - 4}L${p.cx + 3.2} ${yb - 7}Z" fill="url(#${u}-buckle)" stroke="rgb(0 0 0 / 0.22)" stroke-width="0.6"/>`;
          p.buckleBottom = yb + h;
        }
        if (!lining) svg += `<path d="${d}" transform="translate(2.5 3.5)" fill="url(#${u}-lining)"/>`;
        svg += `<path d="${d}" fill="url(#${fillId})" stroke="${edge}" stroke-width="1"/>`;
        svg += `<path d="${d}" fill="url(#${u}-sheen)" style="mix-blend-mode: soft-light"/>`;
        if (padded) svg += `<path d="${d}" fill="url(#${u}-pad)" mask="url(#${u}-padmask)"/>`;
        {
          const plain = /no stitch|ohne naht/i.test(style);
          let seamPath = plain ? '' : outline(p, 5.5);
          // Edge bars: two straight stitches running in from each edge, wrapping over it (lug end for half stitch, above the buckle).
          const bars = (y) => {
            const e = widthAt(p, y) / 2 - 0.5; const len = 4.5 * k;
            return [-1, 1].map((side) => `M${p.cx + side * e} ${y}H${p.cx + side * (e - len)}`).join('');
          };
          // The fold bars sit where the end folds back: just above the buckle, or about 9 mm up from a pinned end.
          const foldY = y1 - p.len * 0.07;
          let barPath = p.keepers ? bars(foldY) : '';
          if (plain) {
            // No stitch: clean edges, but the buckle fold is still held by its two short bars.
          } else if (/half|halbe/i.test(style)) {
            barPath = bars(y0 + 6.5 * k) + barPath;
            seamPath = '';
          } else if (p.keepers || p.pin) {
            // Square ends: the side seams run down and stop – at the second keeper on a buckle piece,
            // just above the spring bar on a deployant piece; they never turn across the end. Short bars hold a buckle fold.
            const stopY = p.pin ? y1 - 7 * k : y1 - p.len * 0.16; const ws = widthAt(p, stopY) / 2 - 5.5; const top = y0 + 9.5;
            const bendY = Math.min(y0 + taperLen(p), stopY); const wb = widthAt(p, bendY) / 2 - 5.5;
            seamPath = `M${p.cx - W / 2 + 5.5} ${top}L${p.cx - wb} ${bendY}L${p.cx - ws} ${stopY}M${p.cx + W / 2 - 5.5} ${top}L${p.cx + wb} ${bendY}L${p.cx + ws} ${stopY}`;
          }
          const st = seamPath ? this.stitches(seamPath) : '';
                    if (st) svg += `<path d="${st}" fill="none" stroke="rgb(0 0 0 / 0.3)" stroke-width="1.9" stroke-linecap="round" transform="translate(0.45 0.7)"/>`;
          if (barPath) svg += `<path d="${barPath}" fill="none" stroke="rgb(0 0 0 / 0.35)" stroke-width="3" stroke-linecap="round" transform="translate(0.5 0.9)"/>`;
          threadFor[pi] = (st ? `<path d="${st}" fill="none" data-preview-thread stroke="#e9dfcc" stroke-width="1.9" stroke-linecap="round"/>` : '')
            + (barPath ? `<path d="${barPath}" fill="none" data-preview-thread stroke="#e9dfcc" stroke-width="3" stroke-linecap="round"/>` : '');
        }
        svg += `{{thread-${pi}}}`;
        if (p.holes) {
          for (let h = 0; h < 7; h += 1) {
            const hy = y0 + p.len * 0.42 + h * p.len * 0.062;
            svg += `<ellipse cx="${p.cx}" cy="${hy}" rx="2.6" ry="3.4" fill="#1d1712" fill-opacity="0.85"/><ellipse cx="${p.cx}" cy="${hy + 0.9}" rx="2.6" ry="3" fill="none" stroke="rgb(255 255 255 / 0.18)" stroke-width="0.8"/>`;
          }
        } else if (!lining && p.keepers) {
          // Two keepers just above the buckle end, a touch wider than the strap they wrap.
          [0.3, 0.16].forEach((at) => {
            const ky = y1 - p.len * at;
            const kw = widthAt(p, ky) + 7; const kh = Math.max(12, p.len * 0.062);
            svg += `<rect x="${p.cx - kw / 2}" y="${ky}" width="${kw}" height="${kh}" rx="4" fill="url(#${fillId})" stroke="${edge}"/>`;
            svg += `<rect x="${p.cx - kw / 2}" y="${ky}" width="${kw}" height="${kh}" rx="4" fill="url(#${u}-sheen)" style="mix-blend-mode: soft-light"/>`;
            svg += `<path d="M${p.cx - kw / 2 + 4} ${ky + kh - 2.5}H${p.cx + kw / 2 - 4}" stroke="rgb(0 0 0 / 0.18)" stroke-width="1"/>`;
          });
        }
        if (lining && adapter) {
          // The end piece from behind: a dark metal block across the strap end, the quick-release bar running through it.
          const bw = W * 0.92; const bh = 8 * k; const bx = p.cx - bw / 2; const by = y0 + 1;
          svg += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="2.5" fill="#5d5d5a" stroke="rgb(0 0 0 / 0.45)" stroke-width="0.7"/><path d="M${bx + 3} ${by + 1.6}H${bx + bw - 3}" stroke="rgb(255 255 255 / 0.25)" stroke-width="0.8" stroke-linecap="round"/>`;
          const ry = by + bh / 2; const rx = p.cx - W / 2 - 5;
          svg += `<rect x="${rx}" y="${ry - 2}" width="${W + 10}" height="4" rx="2" fill="#c4c4c1" stroke="rgb(0 0 0 / 0.25)" stroke-width="0.6"/>`;
          svg += `<rect x="${p.cx - bw / 2 + 6}" y="${ry - 1}" width="3.4" height="${bh / 2 + 6}" rx="1.2" fill="#b0b0ad" stroke="rgb(0 0 0 / 0.3)" stroke-width="0.6"/><circle cx="${p.cx - bw / 2 + 7.7}" cy="${ry + bh / 2 + 5}" r="2.6" fill="#d7d7d4" stroke="rgb(0 0 0 / 0.3)" stroke-width="0.6"/>`;
        } else if (lining && quickSwitch) {
          // QuickSwitch from behind: the metal release block set into the lug end.
          const bw = W * 0.62; const bh = 7 * k; const bx = p.cx - bw / 2; const by = y0 + 1;
          svg += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="2" fill="#c9c9c6" stroke="rgb(0 0 0 / 0.3)" stroke-width="0.6"/><rect x="${p.cx - bw * 0.28}" y="${by + bh * 0.3}" width="${bw * 0.56}" height="${bh * 0.32}" rx="1" fill="#8e8e8b"/>`;
        } else if (lining) {
          // Spring bars through the lug ends; quick-release bars carry a small lever.
          const bx = p.cx - W / 2 - 6; const by = y0 + 6;
          svg += `<rect x="${bx}" y="${by - 2.4}" width="${W + 12}" height="4.8" rx="2.4" fill="#c4c4c1" stroke="rgb(0 0 0 / 0.25)" stroke-width="0.6"/><path d="M${bx + 2} ${by - 1}H${bx + W + 10}" stroke="rgb(255 255 255 / 0.7)" stroke-width="0.8" stroke-linecap="round"/>`;
          if (quick) svg += `<rect x="${p.cx - W / 2 + 8}" y="${by - 1}" width="3.4" height="11" rx="1.2" fill="#b0b0ad" stroke="rgb(0 0 0 / 0.3)" stroke-width="0.6"/><circle cx="${p.cx - W / 2 + 9.7}" cy="${by + 11}" r="2.6" fill="#d7d7d4" stroke="rgb(0 0 0 / 0.3)" stroke-width="0.6"/>`;
        }
        // Small metal ends showing at the sides: the QuickSwitch bar at the lug, and the clasp pin at a square end.
        const nubs = (y, h) => [-1, 1].map((side) => {
          const x = p.cx + side * widthAt(p, y) / 2;
          return `<rect x="${side < 0 ? x - 2.4 * k : x - 0.6}" y="${y}" width="${2.4 * k + 0.6}" height="${h}" rx="1.2" fill="#bdbdba" stroke="rgb(0 0 0 / 0.28)" stroke-width="0.6"/>`;
        }).join('');
        if (quickSwitch) svg += nubs(y0 + 1.5 * k, 3.4 * k);
        if (p.pin) {
          // Deployant end: a quick-release spring bar through the square end, as on our buckle straps.
          // From the front only its ends show; from behind the whole bar and its release lever.
          const py = y1 - 4.5 * k; const ew = widthAt(p, py); const bx = p.cx - ew / 2 - 6;
          const bar = (x, width) => `<rect x="${x}" y="${py - 2.4}" width="${width}" height="4.8" rx="2.4" fill="#c4c4c1" stroke="rgb(0 0 0 / 0.25)" stroke-width="0.6"/>`;
          if (lining) {
            svg += bar(bx, ew + 12) + `<path d="M${bx + 2} ${py - 1}H${bx + ew + 10}" stroke="rgb(255 255 255 / 0.7)" stroke-width="0.8" stroke-linecap="round"/>`;
            svg += `<rect x="${p.cx - ew / 2 + 8}" y="${py - 10}" width="3.4" height="11" rx="1.2" fill="#b0b0ad" stroke="rgb(0 0 0 / 0.3)" stroke-width="0.6"/><circle cx="${p.cx - ew / 2 + 9.7}" cy="${py - 10}" r="2.6" fill="#d7d7d4" stroke="rgb(0 0 0 / 0.3)" stroke-width="0.6"/>`;
          } else {
            svg += bar(bx, 7) + bar(p.cx + ew / 2 - 1, 7);
          }
        }
        if (p.tongue && !lining) svg += p.tongue; // from behind, the tongue is hidden by the strap
      });
      // Thread goes on top of each piece's texture but under keepers and buckle tongue.
      svg = svg.replace('{{thread-0}}', threadFor[0] || '').replace('{{thread-1}}', threadFor[1] || '');
      qs('[data-preview-defs]', this).innerHTML = defs;
      qs('[data-preview-pieces]', this).innerHTML = svg;

      // Technical-drawing style measurements: lug width above each piece, buckle width at both narrow ends, lengths at the sides.
      const line = (x1, y1, x2, y2) => `<path d="M${x1} ${y1}L${x2} ${y2}"/>`;
      const text = (x, y, s, anchor = 'middle') => `<text x="${x}" y="${y}" text-anchor="${anchor}" dominant-baseline="middle">${s}</text>`;
      const span = (cx, half, y, label, below) => line(cx - half, y, cx + half, y) + line(cx - half, y - 5, cx - half, y + 5) + line(cx + half, y - 5, cx + half, y + 5) + text(cx, below ? y + 15 : y - 13, label);
      const [longP, shortP] = pieces;
      let dims = '';
      pieces.forEach((p) => { dims += span(p.cx, W / 2, y0 - 16, `${this.dataset.lugLabel} ${lug} mm`); });
      const endLabel = claspStep ? `${buckleMm} mm` : `${this.dataset.buckleLabel} ${buckleMm} mm`;
      dims += span(longP.cx, w / 2, y0 + longP.len + 20, endLabel, true);
      dims += span(shortP.cx, w / 2, (shortP.buckleBottom || y0 + shortP.len) + 20, endLabel, true);
      const lx = longP.cx - W / 2 - 26; const sx = shortP.cx + W / 2 + 26;
      dims += line(lx, y0, lx, y0 + longP.len) + line(lx - 5, y0, lx + 5, y0) + line(lx - 5, y0 + longP.len, lx + 5, y0 + longP.len);
      dims += text(lx - 10, y0 + longP.len / 2, `${longMm} mm`, 'end');
      dims += line(sx, y0, sx, y0 + shortP.len) + line(sx - 5, y0, sx + 5, y0) + line(sx - 5, y0 + shortP.len, sx + 5, y0 + shortP.len);
      dims += text(sx + 10, y0 + shortP.len / 2, `${shortMm} mm`, 'start');
      qs('[data-preview-dimensions]', this).innerHTML = dims;

      const leather = this.value('leather');
      const colour = leatherUrl ? this.value('leather_colour') : (this.value('leather_colour') || this.dataset.onRequest);
      const stitching = /no stitch|ohne naht/i.test(style) ? '' : this.value('stitching');
      const liningName = this.value('lining');
      qs('[data-preview-caption]', this).textContent = (lining
        ? [liningName, stitching && `${stitching} ${this.dataset.stitching}`, this.value('spring_bar')]
        : [leather, colour, stitching && `${stitching} ${this.dataset.stitching}`]).filter(Boolean).join(' · ');

      const threadUrl = this.photo('stitching', 160);
      this.threadColour(threadUrl).then((rgb) => {
        if (threadUrl !== this.photo('stitching', 160)) return;
        qsa('[data-preview-thread]', this).forEach((s) => s.setAttribute('stroke', rgb || '#e9dfcc'));
      });
    }
  }
  customElements.define('strap-preview', StrapPreview);


  /* ---------- Predictive search ---------- */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-search-results]', this);
      this.defaultResults = this.results?.innerHTML || '';
      this.enabled = this.dataset.predictive === 'true';
      qsa('[data-search-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      this.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
      if (this.enabled) this.input.addEventListener('input', debounce(() => this.search(), 280));
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-search-open]');
        if (!opener) return;
        e.preventDefault();
        this.open(opener);
      });
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      document.dispatchEvent(new CustomEvent('drawer:open'));
      setTimeout(() => trapFocus(qs('.search-modal__panel', this), this.input), 50);
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      releaseFocus();
      this.opener?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('drawer:close'));
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = this.defaultResults; return; }
      this.abort?.abort();
      this.abort = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article,query&resources[limit]=8&resources[limit_scope]=each&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.results.innerHTML = doc.querySelector('[data-predictive-search]')?.outerHTML || '';
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  customElements.define('search-modal', SearchModal);

  /* ---------- Collection filters (Shopify Storefront Filtering) ---------- */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', debounce((e) => {
        const form = e.target.form || e.target.closest('form[data-filter-form]');
        if (form && form.hasAttribute('data-filter-form')) this.apply(form);
      }, 450));
      this.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-filter-form]');
        if (form) { e.preventDefault(); this.apply(form); }
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-filter-link]');
        if (link) { e.preventDefault(); this.load(link.href); }
      });
      window.addEventListener('popstate', () => this.load(window.location.href, false));
    }
    apply(form) {
      const params = new URLSearchParams(new FormData(form));
      // strip empty price params
      [...params.keys()].forEach((k) => { if (!params.get(k)) params.delete(k); });
      this.load(`${window.location.pathname}?${params.toString()}`);
    }
    async load(url, push = true) {
      const grid = qs('[data-results]', this);
      grid?.classList.add('is-loading-results');
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      try {
        const res = await fetch(u.toString());
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('collection-filters');
        if (next) {
          // Remember what had focus (a filter checkbox, price field or the sort select) so the
          // keyboard position survives the re-render.
          const active = document.activeElement;
          const focusKey = active && this.contains(active)
            ? (active.id ? `#${CSS.escape(active.id)}` : active.name ? `[name="${CSS.escape(active.name)}"][value="${CSS.escape(active.value)}"]` : null)
            : null;
          const openDrawerId = qs('theme-drawer.is-open', this)?.id;
          if (openDrawerId) releaseFocus();

          this.innerHTML = next.innerHTML;

          if (openDrawerId) {
            // Re-open properly (aria-hidden, focus trap, opener state) instead of only restoring the class.
            const drawer = document.getElementById(openDrawerId);
            drawer?.open(qs(`[data-drawer-open="${openDrawerId}"]`, this));
          }
          const target = focusKey && qs(focusKey, openDrawerId ? document.getElementById(openDrawerId) : this);
          if (target) setTimeout(() => target.focus({ preventScroll: true }), 80);
        }
        if (push) { u.searchParams.delete('section_id'); window.history.pushState({}, '', u.toString()); }
      } catch (err) {
        window.location.href = url;
      }
    }
  }
  customElements.define('collection-filters', CollectionFilters);

  /* ---------- Horizontal slider controls ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = qs('.slider', this);
      this.prev = qs('[data-slider-prev]', this);
      this.next = qs('[data-slider-next]', this);
      if (!this.track) return;
      const step = () => (this.track.firstElementChild?.getBoundingClientRect().width || 300) + 24;
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        if (this.prev) this.prev.disabled = this.track.scrollLeft < 8;
        if (this.next) this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 8;
      };
      this.track.addEventListener('scroll', debounce(update, 60), { passive: true });
      update();
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Analytics: custom events for Shopify customer events / pixels ----------
     Published through Shopify's own analytics bus, so a custom pixel (Settings, Customer events)
     can forward them to GA4 or similar, subject to the visitor's consent. */
  const track = (name, data = {}) => {
    try { window.Shopify?.analytics?.publish?.(`atelier_${name}`, data); } catch (e) { /* analytics must never break the page */ }
  };
  window.theme.track = track;
  document.addEventListener('submit', (e) => {
    if (e.target.id === 'FooterNewsletter') track('newsletter_submitted', { source: 'footer' });
  });
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-track]');
    if (el) track(el.dataset.track, { label: el.dataset.trackLabel || el.textContent.trim().slice(0, 80), url: el.getAttribute('href') || '' });
  });

  /* ---------- Welcome offer (first-order invitation) ----------
     Opens after a delay or on exit intent, never on arrival and never over another dialog,
     drawer or open menu. Closing snoozes it; signing up hides it for good. */
  class WelcomeOffer extends HTMLElement {
    connectedCallback() {
      this.dialog = qs('dialog', this);
      if (!this.dialog) return;
      this.key = 'atelier:welcome';
      qsa('[data-welcome-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      // Handle Escape ourselves: browsers may ignore it for dialogs opened without a user gesture.
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
      this.dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); this.close(); } });
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); }); // backdrop
      qs('form', this)?.addEventListener('submit', () => { this.store({ subscribed: true }); track('welcome_offer_submitted'); });

      // Back from a successful signup: show the code straight away.
      if (qs('[data-welcome-success]', this)) { this.store({ subscribed: true }); this.open(true); return; }
      if (qs('.welcome-offer__error', this)) { this.open(true); return; }

      const state = this.read();
      if (state.subscribed || (state.until && Date.now() < state.until)) return;

      const delay = Math.max(10, Number(this.dataset.delay) || 30) * 1000;
      // If something else is on screen when the delay ends (cookie banner, cart, menu), try again shortly.
      const attempt = () => { if (!this.open()) this.timer = setTimeout(attempt, 5000); };
      this.timer = setTimeout(attempt, delay);
      if (this.dataset.exitIntent === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.onLeave = (e) => { if (!e.relatedTarget && e.clientY <= 0) this.open(); };
        // Give the visitor a moment before exit intent counts.
        setTimeout(() => document.addEventListener('mouseout', this.onLeave), 8000);
      }
    }
    read() { try { return JSON.parse(localStorage.getItem(this.key)) || {}; } catch (e) { return {}; } }
    store(v) { try { localStorage.setItem(this.key, JSON.stringify({ ...this.read(), ...v })); } catch (e) { /* private mode */ } }
    busy() {
      // The visitor's cookie choice comes first: Shopify's Customer Privacy API (used by the consent app)
      // reports whether its banner still needs an answer.
      // shouldShowBanner() only says the region needs a banner; the choice itself is in
      // currentVisitorConsent(), whose values stay empty until the visitor answers.
      const privacy = window.Shopify?.customerPrivacy;
      let consentPending = false;
      if (typeof privacy?.shouldShowBanner === 'function' && privacy.shouldShowBanner()) {
        const c = privacy.currentVisitorConsent?.() || {};
        consentPending = [c.marketing, c.analytics, c.preferences].some((v) => v === '' || v === undefined);
      }
      // The privacy API is not always loaded on the storefront, so also check whether Shopify's cookie
      // banner is actually on screen. While it is, the cookie choice comes first.
      const banner = document.getElementById('shopify-pc__banner');
      if (banner) {
        const r = banner.getBoundingClientRect();
        const cs = getComputedStyle(banner);
        if (r.height > 0 && r.width > 0 && cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0') consentPending = true;
      }
      return consentPending || document.body.classList.contains('overflow-hidden') || qs('.header__menu li.is-open') || qs('dialog[open]') || document.hidden;
    }
    open(force = false) {
      if (this.dialog.open) return true;
      if (!force && this.busy()) return false;
      clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      this.dialog.showModal();
      track('welcome_offer_viewed');
      return true;
    }
    snooze() {
      const days = Number(this.dataset.snoozeDays) || 21;
      if (!this.read().subscribed) this.store({ until: Date.now() + days * 86400000 });
      track('welcome_offer_dismissed');
    }
    close() { this.snooze(); this.dialog.close(); }
  }
  customElements.define('welcome-offer', WelcomeOffer);

  /* ---------- Visitor country (for the complimentary shipping threshold) ----------
     The .com storefront defaults to the United States for every visitor, so Liquid cannot tell where the visitor is.
     Shopify's detected country decides: 250 for the USA and its territories, 200 everywhere else. */
  const US_REGIONS = ['US', 'PR', 'GU', 'VI', 'AS', 'MP', 'UM'];
  let visitorCountryPromise;
  const visitorCountry = () => {
    if (!visitorCountryPromise) {
      visitorCountryPromise = (async () => {
        try { const saved = sessionStorage.getItem('at_country'); if (saved) return saved; } catch (e) { /* storage unavailable */ }
        try {
          const res = await fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } });
          const code = (await res.json())?.detected_values?.country?.handle || '';
          try { if (code) sessionStorage.setItem('at_country', code); } catch (e) { /* storage unavailable */ }
          return code;
        } catch (e) { return ''; }
      })();
    }
    return visitorCountryPromise;
  };
  const localizeThreshold = (root = document) => {
    const els = qsa('[data-fs-threshold]', root);
    if (!els.length) return;
    visitorCountry().then((code) => {
      if (!code) return;
      els.forEach((el) => {
        const us = Number(el.dataset.fsUs);
        const value = us > 0 && US_REGIONS.includes(code) ? us : Number(el.dataset.fsDefault);
        if (value) el.textContent = (el.dataset.fsFormat || '€[amount]').replace('[amount]', value);
      });
    });
  };
  localizeThreshold();
  document.addEventListener('shopify:section:load', (e) => localizeThreshold(e.target));

  /* ---------- Geolocation ----------
     The .com storefront treats every visitor as being in the United States (USD) until a country is chosen.
     Once per session, hand Shopify the detected country instead: it then shows that market's currency,
     or moves a visitor from Germany to ateliertaha.de. Crawlers and the theme editor are left alone. */
  (() => {
    const form = document.getElementById('GeoLocalizationForm');
    if (!form || window.Shopify?.designMode || /bot|crawl|spider|slurp|lighthouse|headless/i.test(navigator.userAgent)) return;
    try { if (sessionStorage.getItem('at_geo_done')) return; sessionStorage.setItem('at_geo_done', '1'); } catch (e) { return; }
    visitorCountry().then((code) => {
      if (!code || code === window.Shopify?.country) return;
      form.querySelector('[name="country_code"]').value = code;
      form.querySelector('[name="return_to"]').value = window.location.pathname + window.location.search;
      form.submit();
    });
  })();

  /* ---------- "From" prices Liquid could not reach ----------
     snippets/from-price leaves a leather empty when it is not among the product's first 250 variants.
     Fetch the variants in the page's country context (same currency) and write the lowest price of that leather. */
  const variantPrices = {};
  const fetchVariantPrices = async (handle) => {
    const query = `query($h: String!, $a: String, $c: CountryCode) @inContext(country: $c) {
      product(handle: $h) { variants(first: 250, after: $a) { pageInfo { hasNextPage endCursor }
        nodes { selectedOptions { value } price { amount } } } } }`;
    const nodes = [];
    let after = null;
    do {
      const res = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables: { h: handle, a: after, c: window.Shopify?.country || null } }),
      });
      const page = (await res.json())?.data?.product?.variants;
      if (!page) break;
      nodes.push(...page.nodes);
      after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
    } while (after);
    return nodes;
  };
  const fillFromPrices = (root = document) => {
    qsa('[data-from-price][data-leather]', root).filter((el) => !el.textContent.trim()).forEach((el) => {
      const { handle, leather, sample } = el.dataset;
      variantPrices[handle] ||= fetchVariantPrices(handle).catch(() => []);
      variantPrices[handle].then((nodes) => {
        const prices = nodes.filter((n) => n.selectedOptions.some((o) => o.value === leather)).map((n) => Number(n.price.amount));
        if (!prices.length || !sample) return;
        const min = Math.min(...prices);
        // sample is 1 unit in the shop's own format ("€1", "$1"): swap the 1 for the amount.
        el.textContent = sample.replace('1', Number.isInteger(min) ? String(min) : min.toFixed(2));
      });
    });
  };
  fillFromPrices();
  document.addEventListener('shopify:section:load', (e) => fillFromPrices(e.target));

  /* ---------- Shipping progress (complimentary shipping threshold) ----------
     The threshold is set in the store currency; convert it for visitors paying in another currency
     using Shopify's presentment rate, then format in their currency. */
  class ShippingProgress extends HTMLElement {
    connectedCallback() {
      this.render();
      visitorCountry().then((code) => {
        if (!code) return;
        const us = Number(this.dataset.thresholdUs);
        const value = us > 0 && US_REGIONS.includes(code) ? us : Number(this.dataset.thresholdDefault);
        if (!value || value === Number(this.dataset.threshold)) return;
        this.dataset.threshold = value;
        this.classList.remove('is-complete');
        this.render();
      });
    }
    render() {
      const rate = Number(window.Shopify?.currency?.rate) || 1;
      const currency = window.Shopify?.currency?.active || this.dataset.currency;
      const threshold = Math.round(Number(this.dataset.threshold) * rate); // cents, presentment currency
      const total = Number(this.dataset.total); // cents, presentment currency
      if (!threshold || Number.isNaN(total)) return;
      const fmt = (cents) => {
        const digits = cents % 100 ? 2 : 0; // "€36" rather than "€36.00"
        return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(cents / 100);
      };
      const remaining = Math.max(0, threshold - total);
      const bar = qs('.shipping-progress__bar span', this);
      const text = qs('[data-shipping-text]', this);
      if (bar) bar.style.transform = `scaleX(${Math.min(1, total / threshold)})`;
      if (remaining === 0) { this.classList.add('is-complete'); text.textContent = this.dataset.done; track('free_shipping_reached', { total: total / 100, currency }); }
      else text.textContent = this.dataset.remaining.replace('[amount]', fmt(remaining));
      qs('.shipping-progress__bar', this)?.setAttribute('aria-valuenow', String(Math.round(Math.min(1, total / threshold) * 100)));
      this.hidden = false;
      this.updateSwanco(remaining, fmt);
    }
    // Swanco add-ons in the same cart: say what is left to complimentary shipping and put the pieces that cover it first.
    updateSwanco(remaining, fmt) {
      const box = qs('[data-swanco-addons]', this.closest('[data-cart-drawer-inner], [data-main-cart]') || document);
      if (!box) return;
      const text = qs('[data-swanco-text]', box);
      if (text) text.textContent = remaining > 0 ? text.dataset.textRemaining.replace('[amount]', fmt(remaining)) : text.dataset.text;
      const items = qsa('[data-swanco-price]', box);
      items.forEach((li) => { const note = qs('[data-swanco-note]', li); if (note) note.hidden = !(remaining > 0 && Number(li.dataset.swancoPrice) >= remaining); });
      items.filter((li) => !qs('[data-swanco-note]', li)?.hidden).reverse().forEach((li) => li.parentElement.prepend(li));
    }
  }
  customElements.define('shipping-progress', ShippingProgress);

  /* ---------- Store-currency amounts (e.g. the shipping threshold) shown in the visitor's currency ---------- */
  const localizeShopMoney = (root = document) => {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const currency = window.Shopify?.currency?.active;
    if (!currency || rate === 1) return;
    qsa('[data-shop-money]', root).forEach((el) => {
      const cents = Math.round(Number(el.dataset.shopMoney) * rate);
      if (Number.isNaN(cents)) return;
      const digits = cents % 100 ? 2 : 0;
      el.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(cents / 100);
    });
  };
  localizeShopMoney();
  document.addEventListener('shopify:section:load', (e) => localizeShopMoney(e.target));

  /* ---------- Reveal on scroll ---------- */
  const revealInit = () => {
    const els = qsa('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  };
  if (window.Shopify && window.Shopify.designMode) { qsa('.reveal').forEach((el) => el.classList.add('is-visible')); } else { revealInit(); }

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', () => { qsa('.reveal').forEach((el) => el.classList.add('is-visible')); });
  document.addEventListener('shopify:block:select', (e) => {
    const configurator = e.target.closest?.('bespoke-configurator');
    if (!configurator?.top) return;
    // Nested steps (leather colour) live inside their host step; open the top-level step that holds the block,
    // and leave it alone when it is already open so picking a choice in the editor never folds the panel.
    const i = configurator.top.findIndex((s) => s.contains(e.target));
    const header = i > -1 && qs('.config-step__header', configurator.top[i]);
    if (header && header.getAttribute('aria-expanded') !== 'true') configurator.openStep(i);
  });
})();
